import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from "@angular/core";
import { ThemeService } from "../theme.service";
import { trigger, state, style, transition, animate } from "@angular/animations";
import { Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { NewsAnimatorModel } from "../news-animator/news-animator.component";
import { MenuItem } from "primeng/api";
import { AuthenticationService } from "src/app/services/authentication.service";
import { filter, map, mergeMap } from "rxjs/operators";
import { LanguageService } from "src/app/core/language.service";
import { INotification } from "src/app/models/notification.model";
import { NotificationsService } from "src/app/services/notifications.service";
import { Subscription } from "rxjs";
import { ToastService } from "src/app/core/toast.service";
import * as moment from "moment";
import { BreadcrumbService } from "../breadcrumb/breadcrumb.service";
import { BreadcrumbModel } from "../breadcrumb/breadcrumb.model";
import { Role } from "src/app/core/enum/Role";
import { AssistantService } from "../../../services/assistant.service";
import { ASSISTANT_ACTIONS } from "../../../core/enum/Assistant";
import { MessageBroadcasterService } from "../../../services/message.service";
import { IWindow, SpeechRecognitionService } from "../../../services/speech-recognition.service";
import { environment } from "src/environments/environment";
import * as uuid from "uuid";

interface Message {
  knowledgebase_id: string;
  user_id: string;
  question: string;
  isComplete: boolean;
  links: string[];
}

@Component({
  // tslint:disable-next-line: component-selector
  selector: "iti-header",
  styleUrls: ["./header.component.scss"],
  templateUrl: "./header.component.html",
  animations: [
    trigger("slideInOut", [
      state(
        "in",
        style({
          "padding-left": "250px",
        })
      ),
      state(
        "out",
        style({
          "padding-left": "0px",
        })
      ),
      transition("in => out", [animate("200ms ease-in")]),
      transition("out => in", [animate("200ms ease-in")]),
    ]),
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild("notificationsContainer") notificationsContainer!: ElementRef;
  @ViewChild("notificationsIcon") notificationsIcon!: ElementRef;
  @ViewChild("headerNewsLabel") headerNewsLabel!: ElementRef;
  @ViewChild("assistantContainer") assistantContainer!: ElementRef;
  @ViewChild("assistantIcon") assistantIcon!: ElementRef;
  @ViewChild("iconButton") iconButton!: ElementRef;
  @ViewChild("iconButton2") iconButton2!: ElementRef;
  pageTitle = "";
  breadcrumb!: any;
  isHeaderVisible = true;
  isNotificationPopupVisible = false;
  newsMessages: Array<NewsAnimatorModel> = [];
  notifications: Array<INotification> = [];
  notificationsCount = 0;
  accountMenuItems: Array<MenuItem> = [];
  username = "";
  notificationsSubscription!: Subscription;
  breadcrumbServiceSubscription!: Subscription;
  isAssistantPopupVisible = false;
  assistantHistory: Array<{ text: string; id: number; isAnswer: boolean; firstContinuousAnswer: boolean }> = [];
  patientRestrictedAccess!: boolean;
  command: string | null = null;
  disableSpeechControls = false;
  textToSpeechEnabled = false;
  isRecording = false;
  isRecordingSubscription!: Subscription;
  userRole = "";

  socket: WebSocket;
  messages: Message[] = [];
  message: string = "";
  userId!: string;
  isWaitingForResponse: boolean = false;
  showMessages: boolean = true;
  public isGenerating: boolean = false;
  public isCancelled: boolean = false;
  public isLoading: boolean = false;
  public isBotTyping: boolean = false;
  public links!: any;

  get selectedLang() {
    return this.translateService.currentLang;
  }

  @HostListener("document:click", ["$event"]) toggleOpen(event: Event) {
    this.isNotificationPopupVisible =
      this.notificationsIcon.nativeElement.contains(event.target) && this.notificationsCount > 0
        ? !this.isNotificationPopupVisible
        : this.notificationsContainer && this.notificationsContainer.nativeElement.contains(event.target)
        ? true
        : false;
    this.isAssistantPopupVisible = this.assistantIcon.nativeElement.contains(event.target)
      ? !this.isAssistantPopupVisible
      : this.assistantContainer && this.assistantContainer.nativeElement.contains(event.target)
      ? true
      : false;
  }

  constructor(
    private themeService: ThemeService,
    private router: Router,
    private authService: AuthenticationService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private languageService: LanguageService,
    private notificationsService: NotificationsService,
    private toastService: ToastService,
    private breadcrumbService: BreadcrumbService,
    private assistanceService: AssistantService,
    private messageService: MessageBroadcasterService,
    private speechRecognitionService: SpeechRecognitionService,
    private cd: ChangeDetectorRef
  ) {
    this.socket = new WebSocket(`${environment.agentUrl}${environment.agentKnowledgebaseId}`);
    this.userId = uuid.v4();
  }

  ngOnInit(): void {
    this.socket.onopen = (event) => {
      console.log("[open] Connection established");
    };

    // Listen for messages
    this.socket.onmessage = (event) => {
      if (this.isGenerating === false) {
        return;
      } else {
        this.showBotMessage(event.data);
      }
    };

    this.socket.onclose = (event) => {
      console.log("WebSocket is closed now.");
      console.log("Reconnecting.");
      this.socket = new WebSocket(`${environment.agentUrl}${environment.agentKnowledgebaseId}`);
    };

    this.socket.onerror = (error) => {
      console.log(`WebSocket error: ${error}`);
      console.log("Reconnecting.");
      this.socket = new WebSocket(`${environment.agentUrl}${environment.agentKnowledgebaseId}`);
    };

    this.breadcrumbServiceSubscription = this.breadcrumbService.onDataUpdated.subscribe((x: Array<BreadcrumbModel>) => {
      this.breadcrumb = x;
    });

    this.accountMenuItems = [
      {
        label: this.translateService.instant("general.logout"),
        routerLink: "/logout",
      },
    ];

    this.newsMessages = [];
    this.notifications = [];
    this.notificationsCount = 0;

    this.addMessageToAssistant(this.translateService.instant("assistantFirstMessage"), true);

    if (this.authService.getTokenModel()) {
      this.username = this.authService.getTokenModel().email.split("@")[0];
      this.userRole = this.authService.getUserRoles()?.[0];
      if (this.userRole === Role.Doctor) {
        const user = this.authService.getUser();
        this.userRole = user.qualification;
      }
    }

    this.patientRestrictedAccess = this.authService.hasRole(Role.Doctor);

    this.translateService.onLangChange.subscribe(() => {
      this.getReadNotifications();
    });

    this.getReadNotifications();

    this.notificationsSubscription = this.notificationsService.notifyNotificationsChanged().subscribe(() => {
      this.getReadNotifications();
    });

    this.messageService.patientCriticalEvent.subscribe((x) => {
      this.getReadNotifications();
    });

    this.isRecordingSubscription = this.speechRecognitionService.isRecording().subscribe((state) => {
      this.isRecording = state;
      this.cd.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.notificationsSubscription.unsubscribe();
    this.breadcrumbServiceSubscription.unsubscribe();
    this.isRecordingSubscription.unsubscribe();
  }

  get isOpen() {
    return this.themeService.animationState;
  }

  pauseNewsAnimation() {
    this.headerNewsLabel.nativeElement.style["animation-play-state"] = "paused";
  }

  continueNewsAnimation() {
    this.headerNewsLabel.nativeElement.style["animation-play-state"] = "running";
  }

  subscribeNavigationEnd() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === "primary"),
        mergeMap((route) => route.data)
      )
      .subscribe((data: any) => {
        try {
          this.pageTitle = data.title;
          window.scrollTo(0, 0);
        } catch (err) {}
      });
  }

  toggleNotifications() {
    this.isNotificationPopupVisible = !this.isNotificationPopupVisible;
  }

  logoutUser() {
    this.authService.logout();
    this.router.navigateByUrl("/login");
  }

  toggleMenu() {
    this.themeService.toggleMenu();
  }

  getLanguageName(lang: string = "") {
    if (lang === "") {
      lang = this.translateService.currentLang;
    }

    let name = "English";
    switch (lang) {
      case "en":
        name = "English";
        break;
      case "el":
        name = "Ελληνικά";
        break;
      case "sv":
        name = "Svenska";
        break;
      case "it":
        name = "Italiano";
        break;
      case "pt":
        name = "Português";
        break;
      case "tr":
        name = "Türkçe";
        break;
      case "es":
        name = "Español";
        break;
    }

    return name;
  }

  getLanguageImage(lang: string = "") {
    if (lang === "") {
      lang = this.translateService.currentLang;
    }

    let position = "0";
    switch (lang) {
      case "en":
        position = "-2182px";
        break;
      case "el":
        position = "-2446px";
        break;
      case "sv":
        position = "-2446px";
        break;
    }

    return position;
  }

  changeLanguage(lang: string) {
    this.languageService.changeLanguage(lang);
  }

  markNotificationAsRead(id: number, type: string, notification: any) {
    this.notificationsService
      .markNotificationAsRead({ id, isCritical: notification.isCritical || false, isRead: true })
      .subscribe(() => {
        this.toastService.successMessage(this.translateService.instant(`headerPage.NotificationMarkedAsRead`));
        if (notification.NotificationType === "ADoctorRequestedASecondOpinion") {
          this.router.navigateByUrl(
            `patients/patient-record/${notification.data.patientFhirId}/careplan/current?itemId=${notification.data.careplanResourceItemId}`
          );
        }
      });
  }

  getReadNotifications() {
    this.notificationsService
      .getAllNotifications(false, undefined, undefined, undefined, undefined, undefined, undefined)
      .subscribe((notifications: any) => {
        // this.notificationsCount = notifications.notifications?.filter((z: any) => !z.isFixed)?.length;
        this.notificationsCount = notifications.notifications?.length;
        this.notifications = notifications.notifications.slice(0, 5);
        this.notifications.forEach((x: any) => {
          x.createdStr = moment(x.createdAt).format("YYYY-MM-DD hh:mm");
        });
      });
  }

  navigateToNotifications() {
    this.toggleNotifications();
    this.router.navigate(["/notifications"]);
  }

  navigateToCriticalNotifications() {
    this.toggleNotifications();
    this.router.navigate(["/critical-notifications"]);
  }

  async executeCommand(text?: string) {
    const message = (text && text !== "" && text) || (this.command && this.command !== "" && this.command) || null;
    if (message && message?.trim().length === 0) {
      return;
    }
    this.isBotTyping = true;
    if (this.socket.readyState === WebSocket.OPEN) {
      const messageObject = {
        knowledgebase_id: environment.agentKnowledgebaseId,
        user_id: this.userId,
        question: message!,
        isComplete: false,
        links: [],
      };

      const messageString = JSON.stringify(messageObject);
      this.socket.send(messageString);
      this.messages.push(messageObject);
      this.disableSpeechControls = true;
      this.isLoading = true;
      this.command = "";
      this.isWaitingForResponse = true;
      this.isGenerating = true;
      this.addMessageToAssistant(message!);
    } else {
      this.disableSpeechControls = false;
      this.isLoading = false;
      this.command = "";
      this.isWaitingForResponse = false;
      this.isGenerating = false;
      this.isBotTyping = false;
      this.toastService.errorMessage("Agent is currently offline. Please try again later.");
      console.error("WebSocket connection is not open.");
    }
  }

  showBotMessage(message: string) {
    this.isBotTyping = false;
    if (message.includes("<RDY>") || (message.includes("@@@links") && this.isCancelled === false)) {
      if (this.gatherURLs(message)) {
        this.links = this.gatherURLs(message);
      }
      return;
    }

    let currentBotMessage = this.messages.find(function (m) {
      return m.user_id === "BOT" && !m.isComplete;
    });

    if (!currentBotMessage) {
      const messageObject: Message = {
        knowledgebase_id: "00239120-0405-0607-0809-0a0b0c0d0e0f",
        user_id: "BOT",
        question: message,
        isComplete: false,
        links: [],
      };
      this.messages.push(messageObject);
    }

    if (message.includes("<EOS>")) {
      if (currentBotMessage) {
        console.log(this.messages);
        currentBotMessage.isComplete = true;
        this.isGenerating = false;
        this.links = [];
        this.isWaitingForResponse = false;
        this.isCancelled = false;
        this.isLoading = false;
        this.disableSpeechControls = false;
        this.addMessageToAssistant(currentBotMessage.question, true);
        this.say(currentBotMessage.question);
      }
    } else {
      if (currentBotMessage) {
        this.addMessageToAssistant(currentBotMessage.question, true, true);
        currentBotMessage.question += message;
      }
    }
  }

  gatherURLs(text: string) {
    let parts = text.split(/\s+/);

    const urls = parts
      .filter((item) => item.includes('href=\\"'))
      .map((item) => {
        let match = item.match(/href=\\"([^\\"]+)\\"/);
        return match ? match[1] : null;
      })
      .filter((url) => url !== null);

    return urls;
  }

  isBotWriting(): boolean {
    const lastMessage = this.messages[this.messages.length - 1];
    return lastMessage && lastMessage.user_id === "BOT" && !lastMessage.isComplete;
  }

  addMessageToAssistant(message: string, isAnswer = false, isAppend = false) {
    if (!isAppend) {
      if (isAnswer && this.assistantHistory.length > 0) {
        const selectedMessage = this.assistantHistory[this.assistantHistory.length - 1];
        let firstContinuousAnswer = false;
        selectedMessage.isAnswer = isAnswer;
        if (isAnswer && this.assistantHistory.length > 0 && !selectedMessage.isAnswer) {
          firstContinuousAnswer = true;
        }
        selectedMessage.firstContinuousAnswer = firstContinuousAnswer;
      } else {
        let newID = this.assistantHistory ? this.assistantHistory.length + 1 : 0;
        let firstContinuousAnswer = false;
        if (
          isAnswer &&
          this.assistantHistory.length > 0 &&
          !this.assistantHistory[this.assistantHistory.length - 1].isAnswer
        ) {
          firstContinuousAnswer = true;
        }
        this.assistantHistory.push({ text: message, id: newID, isAnswer, firstContinuousAnswer });
        console.log(this.assistantHistory);
      }
    } else {
      if (this.assistantHistory[this.assistantHistory.length - 1].isAnswer === false) {
        let newID = this.assistantHistory ? this.assistantHistory.length + 1 : 0;
        let firstContinuousAnswer = false;
        if (
          isAnswer &&
          this.assistantHistory.length > 0 &&
          !this.assistantHistory[this.assistantHistory.length - 1].isAnswer
        ) {
          firstContinuousAnswer = true;
        }
        this.assistantHistory.push({ text: message, id: newID, isAnswer, firstContinuousAnswer });
      } else {
        this.assistantHistory[this.assistantHistory.length - 1].text = message;
      }
    }
  }

  markNotificationFixed(notification: any) {
    this.notificationsService
      .markNotificationAsFixed({
        id: notification.id,
        isCritical: notification.isCritical || false,
        isFixed: true,
      })
      .subscribe(() => {
        this.toastService.successMessage("toastFixed");
        this.getReadNotifications();
      });
  }

  toggleRecording() {
    if (!this.speechRecognitionService.isAvailable()) {
      this.speechRecognitionService.init(
        (text: string) => {
          this.executeCommand(text);
          this.speechRecognitionService.stop();
          return;
        },
        (error) => {
          console.log(error);
          return null;
        }
      );
    }

    if (!this.isRecording) {
      this.speechRecognitionService.start();
    } else {
      this.speechRecognitionService.stop();
    }
  }

  getMicColor() {
    return this.isRecording ? "#ffbebe" : "white";
  }

  getDisplay(num: number) {
    const result = this.textToSpeechEnabled ? (num === 1 ? "block" : "none") : num === 2 ? "block" : "none";
    console.log(num, result);
    return result;
  }

  toggleTextToSpeech() {
    this.textToSpeechEnabled = !this.textToSpeechEnabled;
    this.iconButton.nativeElement.style.display = this.getDisplay(1);
    this.iconButton2.nativeElement.style.display = this.getDisplay(2);
    if (!this.textToSpeechEnabled) speechSynthesis.cancel();
  }

  say(m: string) {
    try {
      if (this.textToSpeechEnabled) {
        const { SpeechSynthesisUtterance, speechSynthesis }: IWindow = window as IWindow;
        speechSynthesis.cancel();
        var msg = new SpeechSynthesisUtterance(m);
        var voices = window.speechSynthesis.getVoices();
        msg.voice = voices.find((x) => x.voiceURI === "Google UK English Male");
        msg.rate = 1;
        msg.pitch = 1.2;
        msg.text = m;
        msg.lang = "en-US";
        speechSynthesis.speak(msg);
      }
    } catch (error) {
      console.log(error);
    }
  }
}
